import React, { useState, useCallback, useRef } from 'react';
import { CheckCircle2, XCircle, MessageSquare, Send, Paperclip } from 'lucide-react';
import type { BlueprintChecklistItem, BlueprintExitOptions } from '../../../types/processBlueprint';

interface MinhaMesaCompletionBarProps {
  completionType: 'simple_complete' | 'approve_reject';
  exitOptions: BlueprintExitOptions;
  checklist: BlueprintChecklistItem[];
  checklistState: Record<string, boolean>;
  notes: string;
  onNotesChange: (notes: string) => void;
  onComplete: () => void;
  onApprove: () => void;
  onReject: (reason: string) => void;
  isCompleting: boolean;
  isDisabled: boolean;
  canComplete: boolean;
  onSaveNotes?: (note: string, files?: File[]) => void;
  dense?: boolean;
  /** Oculta botões de conclusão inline — ações primárias ficam no dock externo */
  notesOnly?: boolean;
  /** Ref externo para acionar anexo pela action bar */
  attachInputRef?: React.RefObject<HTMLInputElement | null>;
  /** Oculta banner de bloqueio — painel dedicado assume a orientação */
  hideBlockersBanner?: boolean;
  /** Modo bancada de execução — input de notas mais leve */
  executionMode?: boolean;
}

export const MinhaMesaCompletionBar: React.FC<MinhaMesaCompletionBarProps> = ({
  completionType,
  exitOptions,
  checklist,
  checklistState,
  notes,
  onNotesChange,
  onComplete,
  onApprove,
  onReject,
  isCompleting,
  isDisabled,
  canComplete,
  onSaveNotes,
  dense = false,
  notesOnly = false,
  attachInputRef: externalAttachRef,
  hideBlockersBanner = false,
  executionMode = false,
}) => {
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const internalFileRef = useRef<HTMLInputElement>(null);
  const fileInputRef = externalAttachRef ?? internalFileRef;

  const handleSaveNotes = useCallback(() => {
    if (!notes.trim() && pendingFiles.length === 0) return;
    onSaveNotes?.(notes.trim(), pendingFiles.length > 0 ? pendingFiles : undefined);
    onNotesChange('');
    setPendingFiles([]);
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 1500);
  }, [notes, pendingFiles, onSaveNotes, onNotesChange]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      setPendingFiles(prev => [...prev, ...files]);
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // Enter = nova linha; envio somente por botão
  const handleNotesKeyDown = useCallback((_e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // no-op: Enter inserts newline naturally in textarea
  }, []);

  // Validar checklist obrigatorio
  const requiredChecklistItems = checklist.filter((item) => item.required);
  const allRequiredChecked = requiredChecklistItems.every(
    (item) => checklistState[item.id],
  );

  const canSubmit = canComplete && allRequiredChecked && !isDisabled && !isCompleting;

  // Validar justificativa obrigatoria
  const needsJustification = exitOptions.requireJustification && !notes.trim();

  // Contagem de itens pendentes para mensagem específica
  const checkedRequiredCount = requiredChecklistItems.filter((item) => checklistState[item.id]).length;
  const pendingChecklistCount = requiredChecklistItems.length - checkedRequiredCount;

  const handleReject = () => {
    if (!rejectReason.trim()) return;
    onReject(rejectReason.trim());
    setShowRejectInput(false);
    setRejectReason('');
  };

  const hasBlockers = (!allRequiredChecked && requiredChecklistItems.length > 0) || needsJustification || (!canComplete && !isDisabled);

  const dockNotesHeight = executionMode ? 'min-h-[48px]' : 'h-[56px]';

  return (
    <div className={`bg-transparent ${dense ? (notesOnly ? '' : 'px-1 py-2') : 'px-6 py-3'}`}>

      {/* Blocking validation messages — suprimido quando painel dedicado está ativo */}
      {hasBlockers && !hideBlockersBanner ? (
        <div className="mb-1.5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 dark:border-amber-800/40 dark:bg-amber-950/20">
          <span className="mt-0.5 shrink-0 text-[13px]">⛔</span>
          <div className="space-y-0.5">
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-amber-800 dark:text-amber-300">
              Conclusão bloqueada
            </p>
            {!allRequiredChecked && requiredChecklistItems.length > 0 && (
              <p className="font-mono text-[11px] tracking-[0.06em] text-amber-700 dark:text-amber-400">
                Checklist: {pendingChecklistCount} {pendingChecklistCount === 1 ? 'item obrigatório pendente' : 'itens obrigatórios pendentes'} de {requiredChecklistItems.length}
              </p>
            )}
            {!canComplete && !isDisabled && (
              <p className="font-mono text-[11px] tracking-[0.06em] text-amber-700 dark:text-amber-400">
                Campos obrigatórios não preenchidos
              </p>
            )}
            {needsJustification && (
              <p className="font-mono text-[11px] tracking-[0.06em] text-amber-700 dark:text-amber-400">
                Justificativa obrigatória para esta etapa
              </p>
            )}
          </div>
        </div>
      ) : null}

      {/* Reject input (if active) */}
      {showRejectInput && (
        <div className="mb-1.5 flex items-center gap-2">
          <input
            type="text"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Motivo da devolucao..."
            className="flex-1 border border-red-200 bg-white px-3 py-2 text-[13px] text-slate-700 focus:border-red-500 focus:outline-none dark:border-red-800 dark:bg-[var(--bg-surface)] dark:text-slate-300"
            autoFocus
          />
          <button
            onClick={handleReject}
            disabled={!rejectReason.trim()}
            className="border border-red-700 bg-red-600 px-4 py-2 font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Confirmar
          </button>
          <button
            onClick={() => setShowRejectInput(false)}
            className="border border-slate-200 px-3 py-2 font-mono text-[12px] text-slate-500 hover:text-slate-700 dark:border-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Inline: Notes + Actions */}
      <div className={`flex gap-3 ${notesOnly ? 'items-stretch' : 'items-center'}`}>
        <div
          className={`flex min-w-0 flex-1 items-center gap-2 transition-colors ${
            executionMode
              ? `rounded-lg border border-slate-200/80 bg-white px-3 dark:border-slate-700 dark:bg-slate-900/30 ${dockNotesHeight}`
              : `rounded-sm border border-slate-300 bg-white px-4 focus-within:border-slate-500 dark:border-white/[0.08] dark:bg-white/[0.03] dark:focus-within:border-[rgba(0,200,255,0.4)] ${dockNotesHeight}`
          }`}
        >
          <MessageSquare size={executionMode ? 15 : 18} className="shrink-0 text-slate-400" />
          <textarea
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder={
              exitOptions.requireJustification
                ? 'Justificativa obrigatória para concluir...'
                : executionMode
                  ? 'Observação, evidência ou comentário da execução...'
                  : 'Observações, evidências ou comentários da execução...'
            }
            rows={1}
            className={`${dockNotesHeight} w-full resize-none border-0 bg-transparent py-2.5 text-[13px] leading-snug text-slate-700 focus:outline-none dark:text-slate-200`}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="shrink-0 rounded-[4px] p-2 text-slate-500 opacity-60 transition hover:opacity-100 hover:brightness-110 dark:hover:bg-white/[0.05]"
            title="Anexar arquivo"
          >
            <Paperclip size={executionMode ? 15 : 18} />
          </button>
          <button
            type="button"
            onClick={handleSaveNotes}
            disabled={!notes.trim() && pendingFiles.length === 0}
            className={`shrink-0 rounded-[4px] p-2 transition ${notesSaved ? 'text-emerald-400 opacity-100' : 'text-slate-500 opacity-60 hover:opacity-100 hover:brightness-110 dark:hover:bg-white/[0.05]'} disabled:opacity-20 disabled:cursor-not-allowed`}
            title="Enviar observação"
          >
            <Send size={executionMode ? 15 : 18} />
          </button>
        </div>

        {/* Pending files */}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {pendingFiles.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded bg-indigo-50 px-2 py-1 font-mono text-[10px] text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                <Paperclip size={10} />
                {f.name}
                <button type="button" onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))} className="ml-0.5 text-indigo-400 hover:text-indigo-700">&times;</button>
              </span>
            ))}
          </div>
        )}

        {completionType === 'approve_reject' && (
          <button
            onClick={() => setShowRejectInput(true)}
            disabled={isDisabled || isCompleting}
            className={`shrink-0 flex items-center gap-2 border border-red-200 bg-red-50 font-mono text-[12px] font-medium uppercase tracking-[0.12em] text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 ${
              notesOnly ? `${dockNotesHeight} rounded-sm px-3` : 'px-4 py-2.5'
            }`}
          >
            <XCircle size={16} />
            Devolver
          </button>
        )}

        {completionType === 'approve_reject' && !notesOnly && (
          <button
            onClick={onApprove}
            disabled={!canSubmit || needsJustification}
            title={!canSubmit ? 'Preencha todos os campos e checklist obrigatorios' : undefined}
            className="shrink-0 flex items-center gap-2 rounded-md border-2 border-emerald-600 bg-emerald-600 px-8 py-2.5 font-mono text-[13px] font-black uppercase tracking-[0.1em] text-white shadow-lg shadow-emerald-900/30 transition-all hover:bg-emerald-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CheckCircle2 size={18} />
            {isCompleting ? 'Aprovando...' : 'APROVAR ETAPA'}
          </button>
        )}
      </div>
    </div>
  );
};
